import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Header } from '../common/Header';
import Card from '@mui/material/Card';
import TableGrid from '../table-grid/TableGrid';
import { EndPoints } from '../../axios/endPoints';
import { GridColDef } from '@mui/x-data-grid';
import {
  Button,
  Grid,
  IconButton,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Divider,
} from '@mui/material';
import moment from 'moment';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import {
  handleRefreshSelectedMLCreatorTable,
  handleTrainNewModel,
} from '../../store/mlcreator-slice/mlcreator';
import { useTranslation } from 'react-i18next';
import TrainNewModelDialog from './TrainNewModelDialog';
import SuccessfulTrainingDetails from './SuccessfulTrainingDetails';
import TrainingModelDetails from './TrainingModelDetails';
import DeleteTrainingModelDialog from './DeleteTrainingModelDialog';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import FailedTrainingDialog from './FailedTrainingDialog';
import TrainingStatusActions from './TrainingStatusActions';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewTwoToneIcon from '@mui/icons-material/GridViewTwoTone';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { axiosInstance } from '../../axios/axiosHandler';
import qs from 'qs';
import { authHeader } from '../../store/auth-slice/auth-slice';
import { useDebounce } from 'use-debounce';
import { useDisplaySize } from '../../utils/useDisplaySize';
import { classificationRegressionTextColors } from '../../utils/useTextColors';
import TruncatedText from '../common/TruncatedText';
import TruncatedChip from '../common/TruncatedChip';
import TrainingModelShareDialog from './TrainingModelShareDialog';

interface SelectedMLCreatorProps {
  onSelectModel: (modelRow: any, type: 'prediction' | 'optimization') => void;
}

type ViewMode = 'table' | 'cards';

const SelectedMLCreator = React.forwardRef<HTMLDivElement, SelectedMLCreatorProps>((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedCreatorRow = useAppSelector((state: RootState) => state.mlcreator.selectedCreatorRow);
  const refreshSelectedMLCreatorTable = useAppSelector((state: RootState) => state.mlcreator.refreshSelectedMLCreatorTable);
  const { cardsPerPage } = useDisplaySize();
  
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = window.localStorage.getItem('selectedMLCreatorViewMode');
    return saved === 'table' ? 'table' : 'cards';
  });
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearch] = useDebounce(searchInput, 500);
  const [cardPageIndex, setCardPageIndex] = useState<number>(0);
  const [cardData, setCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState<boolean>(false);
  const [cardTotalCount, setCardTotalCount] = useState<number>(0);
  const [cardHasNext, setCardHasNext] = useState<boolean>(false);
  const [cardHasPrevious, setCardHasPrevious] = useState<boolean>(false);
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
  const [shareDialogModel, setShareDialogModel] = useState<any | null>(null);

  const memoizedSelectedCreatorRow = useMemo(() => selectedCreatorRow, [selectedCreatorRow]);

  const handleOpenShareDialog = useCallback((modelRow: any) => {
    setShareDialogModel(modelRow);
    setShareDialogOpen(true);
  }, []);

  const handleCloseShareDialog = useCallback(() => {
    setShareDialogOpen(false);
    setShareDialogModel(null);
    handleRefreshClick();
  }, []);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: t('SELECTED_CREATOR_ID_FIELD') },
      { field: 'name', headerName: t('SELECTED_CREATOR_MODEL_NAME_FIELD'), minWidth: 300 },
      {
        field: 'trainingDate',
        headerName: t('SELECTED_CREATOR_TRAINING_DATE_FIELD'),
        renderCell: (params) => moment.utc(params.value).local().format('DD/MM/YYYY HH:mm'),
        minWidth: 200,
      },
      {
        field: 'selectedModelTypeString',
        headerName: t('SELECTED_CREATOR_MODEL_TYPE_FIELD'),
        renderCell: (params) => t(params.value),
        minWidth: 200,
      },
      {
        field: 'selectedModelSubtype',
        headerName: t('SELECTED_CREATOR_MODEL_SUBTYPE_FIELD'),
        renderCell: (params) => t(params.value),
        minWidth: 250,
      },
      { field: 'testingResultsCount', headerName: t('SELECTED_CREATOR_TESTING_RESULTS_FIELD'), minWidth: 150 },
      {
        field: 'actions',
        headerName: t('SELECTED_CREATOR_ACTIONS_FIELD'),
        minWidth: 360,
        renderCell: (params) => (
          <TrainingStatusActions
            params={params}
            dispatch={dispatch}
            t={t}
            onSelectModel={props.onSelectModel}
            onOpenShare={handleOpenShareDialog}
          />
        ),
      },
    ],
    [dispatch, t, props.onSelectModel, handleOpenShareDialog]
  );

  const fetchCardPage = useCallback(async () => {
    if (!memoizedSelectedCreatorRow) return;
    setCardLoading(true);
    try {
      const queryObj = {
        datasetId: memoizedSelectedCreatorRow.id,
        currentPage: cardPageIndex + 1,
        pageSize: cardsPerPage,
        searchText: debouncedSearch,
        sortColumns: [{ name: 'id', sortDirection: 'asc' }],
      };
      const response = await axiosInstance.get(EndPoints.ML_CREATOR_GET_TRAINING_MODELS, {
        headers: authHeader().headers,
        params: queryObj,
        paramsSerializer: (params) => qs.stringify(params, { encodeValuesOnly: true, allowDots: true }),
      });
      setCardData(response.data.items);
      setCardTotalCount(response.data.totalCount);
      setCardHasNext(response.data.hasNext);
      setCardHasPrevious(response.data.hasPrevious);
    } catch (err) {
      console.error('Error fetching card page:', err);
      setCardData([]);
      setCardTotalCount(0);
    } finally {
      setCardLoading(false);
    }
  }, [memoizedSelectedCreatorRow, cardPageIndex, debouncedSearch, cardsPerPage]);

  useEffect(() => {
    if (viewMode === 'cards' && memoizedSelectedCreatorRow) {
      fetchCardPage();
    }
  }, [viewMode, memoizedSelectedCreatorRow, cardPageIndex, refreshSelectedMLCreatorTable, fetchCardPage]);

  useEffect(() => {
    setCardPageIndex(0);
  }, [memoizedSelectedCreatorRow, cardsPerPage]);

  const handleToggleToTable = useCallback(() => {
    setViewMode('table');
    window.localStorage.setItem('selectedMLCreatorViewMode', 'table');
  }, []);

  const handleToggleToCards = useCallback(() => {
    setViewMode('cards');
    window.localStorage.setItem('selectedMLCreatorViewMode', 'cards');
    setCardPageIndex(0);
  }, []);

  const handleRefreshClick = useCallback(() => {
    dispatch(handleRefreshSelectedMLCreatorTable());
  }, [dispatch]);

  const handleTrainNewModelClick = useCallback(() => {
    dispatch(handleTrainNewModel({ isOpen: true }));
  }, [dispatch]);

  const totalCardPages = Math.ceil(cardTotalCount / cardsPerPage);

  const handleNextPage = useCallback(() => {
    if (cardHasNext) {
      setCardPageIndex((prev) => prev + 1);
    }
  }, [cardHasNext]);

  const handlePrevPage = useCallback(() => {
    if (cardHasPrevious) {
      setCardPageIndex((prev) => prev - 1);
    }
  }, [cardHasPrevious]);

  return (
    <React.Fragment>
      {memoizedSelectedCreatorRow ? (
        <Card ref={ref}>
          <Header
            title={t('SELECTED_CREATOR_ROW', { name: memoizedSelectedCreatorRow.name })}
            helperIcon={<RedirectIconButton to={'/help/3'} className="helper-icon"><ContactSupportIcon /></RedirectIconButton>}
          />
          <Box sx={{ p: 2 }}>
            <SelectedMLCreatorToolbar
              viewMode={viewMode}
              onTrainNewModel={handleTrainNewModelClick}
              onRefresh={handleRefreshClick}
              onToggleToTable={handleToggleToTable}
              onToggleToCards={handleToggleToCards}
              onSearch={setSearchInput}
            />
            {viewMode === 'table' ? (
              <TableView
                columns={columns}
                refresh={refreshSelectedMLCreatorTable}
                datasetId={memoizedSelectedCreatorRow.id}
                searchText={debouncedSearch}
              />
            ) : (
              <Box
                sx={{
                  mt: 2,
                  height: { xs: 300, sm: 375 },
                  display: 'flex',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                }}
              >
                <ModelCardCarousel
                  cardData={cardData}
                  cardLoading={cardLoading}
                  totalCardPages={totalCardPages}
                  hasNext={cardHasNext}
                  hasPrevious={cardHasPrevious}
                  cardPageIndex={cardPageIndex}
                  onNextPage={handleNextPage}
                  onPrevPage={handlePrevPage}
                  onSelectModel={props.onSelectModel}
                  onOpenShare={handleOpenShareDialog}
                />
              </Box>
            )}
          </Box>
          <TrainNewModelDialog />
          <SuccessfulTrainingDetails />
          <TrainingModelDetails />
          <DeleteTrainingModelDialog />
          <TrainingModelShareDialog open={shareDialogOpen} model={shareDialogModel} onClose={handleCloseShareDialog} />
          <FailedTrainingDialog />
        </Card>
      ) : null}
    </React.Fragment>
  );
});

interface SelectedMLCreatorToolbarProps {
  viewMode: ViewMode;
  onTrainNewModel: () => void;
  onRefresh: () => void;
  onToggleToTable: () => void;
  onToggleToCards: () => void;
  onSearch: (text: string) => void;
}

const SelectedMLCreatorToolbar: React.FC<SelectedMLCreatorToolbarProps> = React.memo((props) => {
  const { t } = useTranslation();
  const { viewMode, onTrainNewModel, onRefresh, onToggleToTable, onToggleToCards, onSearch } = props;
  const [searchInput, setSearchInput] = useState<string>('');

  useEffect(() => {
    onSearch(searchInput);
  }, [searchInput, onSearch]);

  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid item xs={12} sm={6}>
        <Button onClick={onTrainNewModel} variant="contained" size="small" className="button-color">
          {t('TRAIN_NEW_MODEL_BUTTON_TEXT')}
        </Button>
        <Tooltip title={t('REFRESH_TABLE_BUTTON_TOOLTIP')}>
          <IconButton onClick={onRefresh} sx={{ ml: 1, color: '#555' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, alignItems: 'center' }}>
        <Box sx={{ mr: 2, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder={t('SELECTED_ML_CREATOR_TOOLBAR_SEARCH')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ width: { xs: '100%', sm: 200 } }}
          />
        </Box>
        <Tooltip title={t('SELECTED_ML_CREATOR_TOOLBAR_SHOW_AS_TABLE')}>
          <IconButton onClick={onToggleToTable} sx={{ color: viewMode === 'table' ? 'var(--link-text-color)' : '#888' }}>
            <TableRowsIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('SELECTED_ML_CREATOR_TOOLBAR_SHOW_AS_CARDS')}>
          <IconButton onClick={onToggleToCards} sx={{ ml: 1, color: viewMode === 'cards' ? 'var(--link-text-color)' : '#888' }}>
            <GridViewTwoToneIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
});
SelectedMLCreatorToolbar.displayName = 'SelectedMLCreatorToolbar';

interface TableViewProps {
  columns: GridColDef[];
  refresh: number;
  datasetId: string;
  searchText: string;
}

const TableView: React.FC<TableViewProps> = React.memo((props) => {
  const { columns, refresh, datasetId, searchText } = props;
  return (
    <Box sx={{ mt: 2, height: { xs: 300, sm: 400, md: 500 }, width: '100%' }}>
      <TableGrid
        baseURL={EndPoints.ML_CREATOR_GET_TRAINING_MODELS}
        aditionalQueryParams={{ datasetId }}
        columns={columns}
        sortColumns={[{ field: 'id', sort: 'asc' }]}
        refresh={refresh}
        disableRowSelection
        tableHeight={500}
        searchText={searchText}
        rowHeight={35}
        columnHeaderHeight={40}
      />
    </Box>
  );
});
TableView.displayName = 'TableView';

interface ModelCardItemProps {
  model: any;
  onSelectModel: (modelRow: any, type: 'prediction' | 'optimization') => void;
  onOpenShare: (modelRow: any) => void;
}

const ModelCardItem: React.FC<ModelCardItemProps> = React.memo((props) => {
  const { t } = useTranslation();
  const { model, onSelectModel, onOpenShare } = props;
  const dispatch = useAppDispatch();
  
  const { color, backgroundColor } = classificationRegressionTextColors[model.selectedModelTypeString];

  return (
    <Card
      sx={{
        flex: '1 0 0',
        p: { xs: 2, sm: 3 },
        mb: 2,
        borderRadius: 2,
        backgroundColor: '#fafafa',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxShadow: 1,
        border: '1px solid rgba(0, 0, 0, 0.15)',
        maxWidth: '100%',
        width: '100%',
      }}
    >
      <Box sx={{ 
        overflowY: 'auto',
        minWidth: 0,
      }}>
        <TruncatedText
          text={model.name || ''}
          maxLength={13}
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: 'var(--link-text-color)',
          }}
        />
        
        <TruncatedText
          text={model.id || ''}
          maxLength={35}
          sx={{ mb: 1, color: '#333' }}
        >
          <strong>{t('SELECTED_CREATOR_ID_FIELD')}:</strong>{' '}
        </TruncatedText>
        
        <Typography
          variant="body2"
          sx={{ 
            mb: 0.5, 
            color: '#333',
            wordBreak: 'break-all',
            overflowWrap: 'break-word',
          }}
        >
          <strong>{t('SELECTED_CREATOR_TRAINING_DATE_FIELD')}:</strong>{' '}
          {moment.utc(model.trainingDate).local().format('DD/MM/YYYY HH:mm')}
        </Typography>
        
        <Box sx={{ mb: 0.5 }}>
          <Typography
            variant="body2"
            component="span"
            sx={{ color: '#333', fontWeight: 'bold' }}
          >
            {t('SELECTED_CREATOR_MODEL_TYPE_FIELD')}:
          </Typography>
          {' '}
          <TruncatedChip
            label={t(model.selectedModelTypeString) || ''}
            maxLength={15}
            sx={{ color, backgroundColor }}
          />
        </Box>
        
        <TruncatedText
          text={t(model.selectedModelSubtype) || ''}
          maxLength={10}
          sx={{ mb: 0.5, color: '#333' }}
        >
          <strong>{t('SELECTED_CREATOR_MODEL_SUBTYPE_FIELD')}:</strong>{' '}
        </TruncatedText>
        
        <Typography
          variant="body2"
          sx={{ 
            mb: 0.5, 
            color: '#333',
            wordBreak: 'break-all',
            overflowWrap: 'break-word',
          }}
        >
          <strong>{t('SELECTED_CREATOR_TESTING_RESULTS_FIELD')}:</strong>{' '}
          {model.testingResultsCount}
        </Typography>
      </Box>
      
      <Divider sx={{ mt: 2 }} />
      <Box sx={{ mt: 2 }}>
        <TrainingStatusActions
          params={{ row: model }}
          dispatch={dispatch}
          t={t}
          onSelectModel={onSelectModel}
          onOpenShare={onOpenShare}
        />
      </Box>
    </Card>
  );
});

ModelCardItem.displayName = 'ModelCardItem';

interface ModelCardCarouselProps {
  cardData: any[];
  cardLoading: boolean;
  totalCardPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
  cardPageIndex: number;
  onNextPage: () => void;
  onPrevPage: () => void;
  onSelectModel: (modelRow: any, type: 'prediction' | 'optimization') => void;
  onOpenShare: (modelRow: any) => void;
}

const ModelCardCarousel: React.FC<ModelCardCarouselProps> = (props) => {
  const { cardData, cardLoading, totalCardPages, hasNext, hasPrevious, cardPageIndex, onNextPage, onPrevPage, onSelectModel, onOpenShare } = props;
  const { t } = useTranslation();

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  const showLoading = useMemo(() => cardLoading, [cardLoading]);
  const showEmpty = useMemo(() => !cardLoading && cardData.length === 0, [cardLoading, cardData.length]);
  
  useEffect(() => {
    if (cardLoading) {
      setIsTransitioning(true);
      setFadeIn(false);
    } else {
      const timer = setTimeout(() => {
          setIsTransitioning(false);
          setFadeIn(true);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [cardLoading]);

  const stableCardData = useMemo(() => cardData, [cardData]);
  
  if (showLoading || isTransitioning) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          opacity: showLoading ? 0.8 : 1,
          transition: 'opacity 0.3s ease-in-out',
          transform: 'translateZ(0)',
        }}
      >
        <CircularProgress 
          style={{ 
            color: 'var(--button-background-color)',
            transform: 'translateZ(0)', 
          }} 
        />
      </Box>
    );
  }
  
  if (showEmpty) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          opacity: fadeIn ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          transform: 'translateZ(0)',
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
              textAlign: 'center', 
              color: '#666',
              transform: 'translateZ(0)',
          }}
        >
          {t('MODEL_CARD_CAROUSEL_NO_DATA_AVAILABLE')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
      <IconButton
        onClick={onPrevPage}
        sx={{ color: totalCardPages > 1 ? 'var(--link-text-color)' : '#ccc' }}
        disabled={totalCardPages <= 1 || !hasPrevious}
      >
        <ChevronLeftIcon />
      </IconButton>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {stableCardData.map((model) => (
            <ModelCardItem key={model.id} model={model} onSelectModel={onSelectModel} onOpenShare={onOpenShare} />
          ))}
        </div>
      </Box>
      <IconButton
        onClick={onNextPage}
        sx={{ color: totalCardPages > 1 ? 'var(--link-text-color)' : '#ccc' }}
        disabled={totalCardPages <= 1 || !hasNext}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default SelectedMLCreator;
